import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';
import { X } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import MapDrawField from './MapDrawField';

const FormDialog = ({ open, onClose, onSubmit, title, fields = [], initialData = {} }) => {
  const [mapData, setMapData] = useState(null);
  
  // Create default values from fields - simpler, safer approach
  const getDefaultValues = () => {
    const defaults = {};
    
    // Ensure fields is an array
    if (!Array.isArray(fields)) {
      console.warn('FormDialog: fields is not an array:', fields);
      return defaults;
    }
    
    // Ensure initialData is an object
    const safeInitialData = initialData || {};
    
    // Iterate safely with try-catch
    fields.forEach((field, index) => {
      try {
        // Multiple layers of null checks
        if (field === null || field === undefined) {
          console.warn(`FormDialog: field at index ${index} is null/undefined`);
          return;
        }
        if (typeof field !== 'object') {
          console.warn(`FormDialog: field at index ${index} is not an object:`, typeof field);
          return;
        }
        if (!('name' in field)) {
          console.warn(`FormDialog: field at index ${index} has no name property:`, field);
          return;
        }
        if (field.type === 'map') {
          return; // Skip map fields
        }
        
        // Set default value - use safeInitialData instead of initialData
        const fieldName = field.name;
        defaults[fieldName] = safeInitialData[fieldName] !== undefined ? safeInitialData[fieldName] : '';
      } catch (error) {
        console.error(`FormDialog: Error processing field at index ${index}:`, error, field);
      }
    });
    
    return defaults;
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (open) {
      const defaults = getDefaultValues();
      reset(defaults);
    }
  }, [open, reset, fields, initialData]); // Include fields and initialData in dependencies

  const onFormSubmit = async (data) => {
    try {
      // Merge map data if available
      const submitData = mapData ? { ...data, ...mapData } : data;
      await onSubmit(submitData);
      reset();
      setMapData(null);
    } catch (error) {
      // Error is handled by parent component
      console.error('Form submission error:', error);
    }
  };

  const handleClose = () => {
    reset();
    setMapData(null);
    onClose();
  };

  const renderField = (field) => {
    // Skip null or invalid fields
    if (!field || !field.name) return null;
    
    const commonProps = {
      name: field.name,
      control,
      rules: field.validation || (field.required ? { required: `${field.label} is required` } : {}),
    };

    // Handle custom map drawing field
    if (field.type === 'map') {
      return (
        <MapDrawField
          value={mapData}
          onChange={setMapData}
          label={field.label}
          helperText={field.helperText}
          disabled={field.disabled}
          existingGeometry={initialData?.geometry}
          singleGeometryOnly={field.singleGeometryOnly}
          onAddressFound={(address) => {
            // Only auto-fill address if the current address field is empty
            const currentAddress = getValues('address');
            if (!currentAddress || currentAddress.trim() === '') {
              setValue('address', address);
            }
          }}
        />
      );
    }

    switch (field.type) {
      case 'hidden':
        // Hidden fields - register but don't render
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField }) => (
              <input type="hidden" {...controllerField} />
            )}
          />
        );

      case 'select':
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => (
              <FormControl fullWidth error={!!fieldState.error}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  {...controllerField}
                  label={field.label}
                  disabled={field.disabled}
                >
                  {field.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
                {!fieldState.error && field.helperText && (
                  <FormHelperText>{field.helperText}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        );

      case 'multiselect':
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => {
              // Ensure value is always an array
              const value = Array.isArray(controllerField.value) ? controllerField.value : [];
              return (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    {...controllerField}
                    multiple
                    value={value}
                    label={field.label}
                    disabled={field.disabled}
                    input={<OutlinedInput label={field.label} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((val) => {
                          const option = field.options?.find(opt => opt.value === val);
                          return (
                            <Chip key={val} label={option?.label || val} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {field.options?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={value.includes(option.value)} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                  {!fieldState.error && field.helperText && (
                    <FormHelperText>{field.helperText}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
        );

      case 'textarea':
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => (
              <TextField
                {...controllerField}
                label={field.label}
                multiline
                rows={field.rows || 4}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || field.helperText}
                disabled={field.disabled}
              />
            )}
          />
        );

      case 'date':
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => (
              <TextField
                {...controllerField}
                label={field.label}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || field.helperText}
                disabled={field.disabled}
              />
            )}
          />
        );

      case 'number':
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => (
              <TextField
                {...controllerField}
                label={field.label}
                type="number"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || field.helperText}
                disabled={field.disabled}
              />
            )}
          />
        );

      default:
        return (
          <Controller
            {...commonProps}
            render={({ field: controllerField, fieldState }) => (
              <TextField
                {...controllerField}
                label={field.label}
                type={field.type || 'text'}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message || field.helperText}
                disabled={field.disabled}
              />
            )}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        {title}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {Array.isArray(fields) && fields
              .filter(field => field && typeof field === 'object' && field.name)
              .map((field) => (
                <Box key={field.name}>{renderField(field)}</Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: 'linear-gradient(135deg, #6C8EBF 0%, #5A7CAD 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5A7CAD 0%, #47638F 100%)',
              },
            }}
          >
            {isSubmitting ? 'Saving...' : initialData?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormDialog;
